import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { useSearchParams } from 'react-router-dom';
import { 
  ChartBarIcon,
  ArrowTrendingUpIcon,
  GlobeAltIcon,
  SparklesIcon,
  UsersIcon,
  CalendarIcon,
  LightBulbIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { ViralPrediction, MomentumAnalysis } from '../types';

// Utility functions
const getViralProbabilityColor = (probability: number) => {
  if (probability > 0.8) return 'text-green-600';
  if (probability > 0.6) return 'text-yellow-600';
  return 'text-red-600';
};

const getMomentumColor = (score: number) => {
  if (score > 70) return 'text-green-600';
  if (score > 40) return 'text-yellow-600';
  return 'text-red-600';
};

const formatPercentage = (num: number) => {
  return `${(num * 100).toFixed(1)}%`;
};

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const AnalyticsPage: React.FC = () => {
  const { isFeatureEnabled, setLoading, addNotification } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viralPredictions, setViralPredictions] = useState<ViralPrediction[]>([]);
  const [momentumAnalysis, setMomentumAnalysis] = useState<MomentumAnalysis[]>([]);
  const [platformAnalytics, setPlatformAnalytics] = useState<any>(null);
  const [categoryAnalytics, setCategoryAnalytics] = useState<any>(null);
  const [competitiveAnalysis, setCompetitiveAnalysis] = useState<any>(null);
  const [marketInsights, setMarketInsights] = useState<any>(null);
  const [loading, setLocalLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'momentum' | 'competitive' | 'viral' | 'audience' | 'forecasting' | 'business'>('overview');
  const [dateRange, setDateRange] = useState('7d');

  // Handle URL parameter for tab switching
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['momentum', 'competitive', 'viral', 'audience', 'forecasting', 'business'].includes(tabParam)) {
      setActiveTab(tabParam as any);
    }
  }, [searchParams]);

  const loadAnalytics = useCallback(async () => {
    setLocalLoading(true);
    setLoading('analytics', true);
    try {
      const [
        viralData,
        momentumData,
        platformData,
        categoryData,
        competitiveData,
        marketData
      ] = await Promise.all([
        apiService.getViralPredictions(),
        apiService.getMomentumAnalysis(),
        apiService.getPlatformAnalytics(),
        apiService.getCategoryAnalytics(),
        apiService.getCompetitiveAnalysis(),
        apiService.getMarketInsights()
      ]);

      // Extract arrays from response objects
      setViralPredictions(viralData?.viral_predictions || viralData || []);
      setMomentumAnalysis(momentumData?.momentum_analysis || momentumData || []);
      setPlatformAnalytics(platformData?.platform_analytics || platformData || null);
      setCategoryAnalytics(categoryData?.category_analytics || categoryData || null);
      setCompetitiveAnalysis(competitiveData?.competitive_analysis || competitiveData || null);
      setMarketInsights(marketData?.market_insights || marketData || null);
    } catch (error) {
      console.error('Error loading analytics:', error);
      addNotification({
        id: 'analytics-error',
        type: 'error',
        title: 'Error Loading Analytics',
        message: 'Failed to load analytics data. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    } finally {
      setLocalLoading(false);
      setLoading('analytics', false);
    }
  }, [setLoading, addNotification]);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, loadAnalytics]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Advanced trend analysis and business intelligence</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 appearance-none bg-white pr-8"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-1 sm:gap-2 lg:gap-4">
          <button
            onClick={() => {
              setActiveTab('overview');
              setSearchParams({});
            }}
            className={`py-2 px-3 border-2 rounded-t-lg font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'overview'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => {
              setActiveTab('momentum');
              setSearchParams({ tab: 'momentum' });
            }}
            className={`py-2 px-3 border-2 rounded-t-lg font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'momentum'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Momentum Analysis
          </button>
          <button
            onClick={() => {
              setActiveTab('competitive');
              setSearchParams({ tab: 'competitive' });
            }}
            className={`py-2 px-3 border-2 rounded-t-lg font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'competitive'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Trend Comparison
          </button>
          <button
            onClick={() => {
              setActiveTab('viral');
              setSearchParams({ tab: 'viral' });
            }}
            className={`py-2 px-3 border-2 rounded-t-lg font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'viral'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Viral Predictions
          </button>
          <button
            onClick={() => {
              setActiveTab('audience');
              setSearchParams({ tab: 'audience' });
            }}
            className={`py-2 px-3 border-2 rounded-t-lg font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'audience'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Audience Analysis
          </button>
          <button
            onClick={() => {
              setActiveTab('forecasting');
              setSearchParams({ tab: 'forecasting' });
            }}
            className={`py-2 px-3 border-2 rounded-t-lg font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'forecasting'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Forecasting
          </button>
          <button
            onClick={() => {
              setActiveTab('business');
              setSearchParams({ tab: 'business' });
            }}
            className={`py-2 px-3 border-2 rounded-t-lg font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'business'
                ? 'border-purple-500 text-purple-600 bg-purple-50'
                : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            Business Intelligence
          </button>
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading analytics...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <OverviewTab 
              viralPredictions={viralPredictions}
              momentumAnalysis={momentumAnalysis}
              platformAnalytics={platformAnalytics}
              categoryAnalytics={categoryAnalytics}
            />
          )}
          {activeTab === 'momentum' && (
            isFeatureEnabled('canAccessAdvancedAnalytics') ? (
              <MomentumAnalysisTab momentumAnalysis={momentumAnalysis} />
            ) : (
              <PlaceholderContent 
                title="Momentum Analysis"
                description="Get detailed momentum analysis to understand trend acceleration and deceleration patterns."
                icon={<ArrowTrendingUpIcon className="h-8 w-8 text-purple-600" />}
                features={[
                  "Real-time momentum scoring (0-100 scale)",
                  "Trend acceleration detection", 
                  "Deceleration pattern analysis",
                  "Custom momentum thresholds",
                  "Historical momentum tracking",
                  "Momentum-based trend classification"
                ]}
                sampleData={[
                  { value: "87%", label: "Average Accuracy" },
                  { value: "24/7", label: "Real-time Updates" },
                  { value: "3.2s", label: "Processing Time" }
                ]}
                tier="Growth"
              />
            )
          )}
          {activeTab === 'competitive' && (
            isFeatureEnabled('canAccessAdvancedAnalytics') ? (
              <CompetitiveAnalysisTab competitiveAnalysis={competitiveAnalysis} />
            ) : (
              <PlaceholderContent 
                title="Trend Comparison"
                description="Compare trends across different categories and time periods to identify opportunities."
                icon={<ChartBarIcon className="h-8 w-8 text-purple-600" />}
                features={[
                  "Cross-category trend analysis",
                  "Time period comparisons",
                  "Opportunity identification",
                  "Competitive benchmarking",
                  "Market gap analysis",
                  "Trend overlap detection"
                ]}
                sampleData={[
                  { value: "15", label: "Categories Compared" },
                  { value: "92%", label: "Accuracy Rate" },
                  { value: "2.1s", label: "Analysis Time" }
                ]}
                tier="Growth"
              />
            )
          )}
          {activeTab === 'viral' && (
            isFeatureEnabled('canAccessForecasting') ? (
              <ViralPredictionsTab viralPredictions={viralPredictions} />
            ) : (
              <UpgradeCTA 
                title="Viral Predictions"
                description="Predict which trends will go viral with our advanced AI-powered forecasting."
                features={[
                  "AI-powered viral prediction (0-100% probability)",
                  "Real-time trend monitoring",
                  "Viral probability scoring",
                  "Peak timing predictions",
                  "14-day advanced forecasting",
                  "Confidence intervals & risk assessment"
                ]}
                tier="Professional"
              />
            )
          )}
          {activeTab === 'audience' && (
            isFeatureEnabled('canAccessBusinessIntelligence') ? (
              <AudienceAnalysisTab />
            ) : (
              <UpgradeCTA 
                title="Audience Analysis"
                description="Deep dive into audience demographics, interests, and engagement patterns."
                features={[
                  "Demographic analysis (age, gender, location)",
                  "Interest mapping & psychographics",
                  "Engagement pattern analysis",
                  "Audience segmentation",
                  "Behavioral trend insights",
                  "Cross-platform audience tracking"
                ]}
                tier="Professional"
              />
            )
          )}
          {activeTab === 'forecasting' && (
            isFeatureEnabled('canAccessForecasting') ? (
              <ForecastingTab />
            ) : (
              <UpgradeCTA 
                title="Forecasting"
                description="Predict future trends and market movements with our advanced forecasting models."
                features={[
                  "14-day advanced trend predictions",
                  "Market movement analysis",
                  "Seasonal pattern detection",
                  "Confidence intervals & risk scores",
                  "Multiple scenario modeling",
                  "Trend lifecycle predictions"
                ]}
                tier="Professional"
              />
            )
          )}
          {activeTab === 'business' && (
            isFeatureEnabled('canAccessBusinessIntelligence') ? (
              <BusinessIntelligenceTab marketInsights={marketInsights} />
            ) : (
              <UpgradeCTA 
                title="Business Intelligence"
                description="Get comprehensive business insights and market intelligence for strategic decision making."
                features={[
                  "Market intelligence reports",
                  "Strategic insights & recommendations",
                  "ROI analysis & impact projections",
                  "Executive dashboards",
                  "Competitive trend analysis",
                  "Market opportunity sizing"
                ]}
                tier="Business"
              />
            )
          )}
        </div>
      )}
    </div>
  );
};

// Overview Tab Component
interface OverviewTabProps {
  viralPredictions: ViralPrediction[];
  momentumAnalysis: MomentumAnalysis[];
  platformAnalytics: any;
  categoryAnalytics: any;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ 
  viralPredictions, 
  momentumAnalysis, 
  platformAnalytics, 
  categoryAnalytics 
}) => {
  // Ensure we have arrays before calling slice
  const topViralTrends = (viralPredictions || []).slice(0, 5);
  const topMomentumTrends = (momentumAnalysis || []).slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
                         <div className="p-2 bg-purple-100 rounded-lg">
               <ArrowTrendingUpIcon className="h-6 w-6 text-purple-600" />
             </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Active Trends</p>
              <p className="text-2xl font-bold text-gray-900">
                {(viralPredictions || []).length + (momentumAnalysis || []).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <SparklesIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">High Viral Potential</p>
              <p className="text-2xl font-bold text-gray-900">
                {(viralPredictions || []).filter(t => t.viral_probability > 0.7).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">High Momentum</p>
              <p className="text-2xl font-bold text-gray-900">
                {(momentumAnalysis || []).filter(t => t.momentum_score > 70).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <GlobeAltIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Platforms Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {platformAnalytics?.platforms?.length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Viral Predictions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Viral Predictions</h2>
        <div className="space-y-4">
          {topViralTrends.map((trend, index) => (
            <div key={trend.trend_name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-purple-600">{index + 1}</span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{trend.trend_name}</h3>
                  <p className="text-sm text-gray-500">
                    Trajectory: {trend.trajectory}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-lg font-semibold ${getViralProbabilityColor(trend.viral_probability)}`}>
                  {formatPercentage(trend.viral_probability)}
                </p>
                                  <p className="text-sm text-gray-500">
                    Peak prediction: {trend.peak_prediction}
                  </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Momentum Trends */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Momentum Trends</h2>
        <div className="space-y-4">
          {topMomentumTrends.map((trend, index) => (
            <div key={trend.trend_name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{trend.trend_name}</h3>
                  <p className="text-sm text-gray-500">
                    Growth rate: {formatPercentage(trend.growth_rate)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-lg font-semibold ${getMomentumColor(trend.momentum_score)}`}>
                  {trend.momentum_score}%
                </p>
                <p className="text-sm text-gray-500 capitalize">
                  {trend.classification}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Viral Predictions Tab Component
interface ViralPredictionsTabProps {
  viralPredictions: ViralPrediction[];
}

const ViralPredictionsTab: React.FC<ViralPredictionsTabProps> = ({ viralPredictions }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Viral Predictions</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Viral Probability
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trajectory
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Peak Prediction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Viral Probability
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Factors
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(viralPredictions || []).map((prediction) => (
                <tr key={prediction.trend_name} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{prediction.trend_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-semibold ${getViralProbabilityColor(prediction.viral_probability)}`}>
                      {formatPercentage(prediction.viral_probability)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prediction.trajectory || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prediction.peak_prediction || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prediction.viral_probability ? formatPercentage(prediction.viral_probability) : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prediction.viral_factors ? Object.keys(prediction.viral_factors).length : 0} factors
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Momentum Analysis Tab Component
interface MomentumAnalysisTabProps {
  momentumAnalysis: MomentumAnalysis[];
}

const MomentumAnalysisTab: React.FC<MomentumAnalysisTabProps> = ({ momentumAnalysis }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Momentum Analysis</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Momentum Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Growth Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acceleration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Classification
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Factors
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(momentumAnalysis || []).map((analysis) => (
                <tr key={analysis.trend_name} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{analysis.trend_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-semibold ${getMomentumColor(analysis.momentum_score)}`}>
                      {analysis.momentum_score}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatPercentage(analysis.growth_rate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {analysis.acceleration_factor.toFixed(2)}x
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                      analysis.classification === 'high' ? 'bg-green-100 text-green-800' :
                      analysis.classification === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {analysis.classification}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(analysis.factors || []).length} factors
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Platform Analytics Tab Component
interface PlatformAnalyticsTabProps {
  platformAnalytics: any;
}

const PlatformAnalyticsTab: React.FC<PlatformAnalyticsTabProps> = ({ platformAnalytics }) => {
  if (!platformAnalytics) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No platform data available</h3>
        <p className="text-gray-600">Platform analytics will appear here when data is available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {(platformAnalytics?.platforms || []).map((platform: any) => (
          <div key={platform.name} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{platform.name}</h3>
              <div className="p-2 bg-blue-100 rounded-lg">
                <ChartBarIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Engagement</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(platform.total_engagement)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Growth Rate</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatPercentage(platform.growth_rate)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Trend Count</span>
                <span className="text-sm font-medium text-gray-900">
                  {platform.trend_count}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Competitive Analysis Tab Component
interface CompetitiveAnalysisTabProps {
  competitiveAnalysis: any;
}

const CompetitiveAnalysisTab: React.FC<CompetitiveAnalysisTabProps> = ({ competitiveAnalysis }) => {
  if (!competitiveAnalysis) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No competitive data available</h3>
        <p className="text-gray-600">Competitive analysis will appear here when data is available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Competitive Landscape</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-3">Direct Competitors</h3>
            <div className="space-y-3">
              {(competitiveAnalysis?.direct_competitors || []).map((competitor: any) => (
                <div key={competitor.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{competitor.name}</p>
                    <p className="text-sm text-gray-500">{competitor.market_share}% market share</p>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    competitor.competitive_position === 'leader' ? 'bg-green-100 text-green-800' :
                    competitor.competitive_position === 'challenger' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {competitor.competitive_position}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-3">Market Gaps</h3>
            <div className="space-y-2">
              {(competitiveAnalysis?.market_gaps || []).map((gap: string, index: number) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">{gap}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Market Insights Tab Component
interface MarketInsightsTabProps {
  marketInsights: any;
}

const MarketInsightsTab: React.FC<MarketInsightsTabProps> = ({ marketInsights }) => {
  if (!marketInsights) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No market data available</h3>
        <p className="text-gray-600">Market insights will appear here when data is available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Market Size & Growth</h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Market Size</span>
              <span className="text-sm font-medium text-gray-900">
                ${formatNumber(marketInsights.market_size || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Growth Rate</span>
              <span className="text-sm font-medium text-gray-900">
                {formatPercentage(marketInsights.growth_rate || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Growth Potential</span>
              <span className="text-sm font-medium text-gray-900">
                {formatPercentage(marketInsights.growth_potential || 0)}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Opportunities</h3>
          <div className="space-y-3">
                          {(marketInsights?.opportunities || []).slice(0, 5).map((opportunity: any, index: number) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{opportunity.description}</p>
                  <p className="text-xs text-gray-500">
                    Potential: ${formatNumber(opportunity.potential_revenue)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder Content Component for Business Users
interface PlaceholderContentProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  sampleData?: any[];
  tier: string;
}

const PlaceholderContent: React.FC<PlaceholderContentProps> = ({ title, description, icon, features, sampleData, tier }) => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          {icon}
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 max-w-2xl mx-auto">{description}</p>
        <div className="mt-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
            {tier} Tier Feature
          </span>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((feature, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-purple-500 rounded-full mr-3 flex-shrink-0"></div>
              <span className="text-gray-700">{feature}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Sample Data Preview */}
      {sampleData && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Preview Data</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sampleData.map((data, index) => (
              <div key={index} className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-purple-600 mb-1">{data.value}</div>
                <div className="text-sm text-gray-600">{data.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Development Status */}
      <div className="text-center py-4">
        <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
          <SparklesIcon className="h-4 w-4 mr-2" />
          In Development - Coming Soon
        </div>
      </div>
    </div>
  );
};

// Upgrade CTA Component
interface UpgradeCTAProps {
  title: string;
  description: string;
  features: string[];
  tier: string;
}

const UpgradeCTA: React.FC<UpgradeCTAProps> = ({ title, description, features, tier }) => {
  const handleUpgrade = () => {
    window.location.href = '/pricing';
  };

  return (
    <div className="text-center py-12">
      <div className="max-w-2xl mx-auto">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="h-8 w-8 text-purple-600" />
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{description}</p>
        
        {/* Features List */}
        <div className="mb-8">
          <h4 className="text-lg font-medium text-gray-900 mb-4">What you'll get:</h4>
          <ul className="text-left space-y-2 max-w-md mx-auto">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <button
          onClick={handleUpgrade}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Upgrade to {tier}
        </button>
      </div>
    </div>
  );
};

// Placeholder tab components
const AudienceAnalysisTab: React.FC = () => (
  <div className="text-center py-12">
    <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">Audience Analysis</h3>
    <p className="text-gray-600">Audience analysis features coming soon</p>
  </div>
);

const ForecastingTab: React.FC = () => (
  <div className="text-center py-12">
    <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">Forecasting</h3>
    <p className="text-gray-600">Forecasting features coming soon</p>
  </div>
);

const BusinessIntelligenceTab: React.FC<{ marketInsights: any }> = ({ marketInsights }) => (
  <div className="text-center py-12">
    <LightBulbIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">Business Intelligence</h3>
    <p className="text-gray-600">Business intelligence features coming soon</p>
  </div>
);

export default AnalyticsPage; 